/**
 * Clipboard Service
 * Handles copy/paste/duplicate operations for nodes and edges
 */

import { Node, Edge } from '@xyflow/react'

export interface ClipboardData {
  nodes: Node[]
  edges: Edge[]
  timestamp: number
}

/**
 * Service for managing clipboard operations in the diagram editor
 */
export class ClipboardService {
  private static instance: ClipboardService
  private clipboardData: ClipboardData | null = null

  private constructor() {}

  static getInstance(): ClipboardService {
    if (!ClipboardService.instance) {
      ClipboardService.instance = new ClipboardService()
    }
    return ClipboardService.instance
  }

  /**
   * Copy selected nodes and their connecting edges to clipboard
   */
  copy(selectedNodes: Node[], allEdges: Edge[]): void {
    if (selectedNodes.length === 0) return

    const selectedNodeIds = new Set(selectedNodes.map(n => n.id))

    // Find edges that connect copied nodes
    const relevantEdges = allEdges.filter(edge =>
      selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target)
    )

    this.clipboardData = {
      nodes: selectedNodes,
      edges: relevantEdges,
      timestamp: Date.now()
    }

    console.log('📋 Copied to clipboard:', {
      nodes: selectedNodes.length,
      edges: relevantEdges.length
    })
  }

  /**
   * Paste clipboard contents with offset
   */
  paste(offset: { x: number; y: number } = { x: 50, y: 50 }): {
    nodes: Node[]
    edges: Edge[]
    idMap: Map<string, string>
  } | null {
    if (!this.clipboardData || this.clipboardData.nodes.length === 0) {
      console.warn('⚠️ Clipboard is empty')
      return null
    }

    // Generate new IDs and create mapping
    const idMap = new Map<string, string>()
    const timestamp = Date.now()
    const randomSuffix = Math.random().toString(36).substring(2, 9)

    // Clone nodes with new IDs and offset positions
    const newNodes = this.clipboardData.nodes.map((node, index) => {
      const newId = `${node.id}-copy-${timestamp}-${index}-${randomSuffix}`
      idMap.set(node.id, newId)

      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + offset.x,
          y: node.position.y + offset.y
        },
        selected: true, // Auto-select pasted nodes
        data: {
          ...node.data,
          // Clear group metadata for pasted nodes to avoid conflicts
          group: undefined
        }
      }
    })

    // Clone edges with new IDs and update source/target
    const newEdges: Edge[] = []

    this.clipboardData.edges.forEach((edge, index) => {
      const newId = `${edge.id}-copy-${timestamp}-${index}-${randomSuffix}`
      const newSource = idMap.get(edge.source)
      const newTarget = idMap.get(edge.target)

      if (!newSource || !newTarget) {
        console.warn('⚠️ Could not map edge:', edge.id)
        return
      }

      newEdges.push({
        ...edge,
        id: newId,
        source: newSource,
        target: newTarget,
        selected: true
      })
    })

    console.log('📋 Pasted from clipboard:', {
      nodes: newNodes.length,
      edges: newEdges.length,
      offset
    })

    return { nodes: newNodes, edges: newEdges, idMap }
  }

  /**
   * Check if clipboard has data
   */
  hasData(): boolean {
    return this.clipboardData !== null && this.clipboardData.nodes.length > 0
  }

  /**
   * Clear clipboard
   */
  clear(): void {
    this.clipboardData = null
  }

  /**
   * Get clipboard data (for debugging)
   */
  getData(): ClipboardData | null {
    return this.clipboardData
  }
}

/**
 * Generate a unique ID for a node or edge
 */
export function generateId(prefix: string = 'node'): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 9)
  return `${prefix}-${timestamp}-${random}`
}

/**
 * Clone a node with a new ID and optional position offset
 */
export function cloneNode(node: Node, offset: { x: number; y: number } = { x: 50, y: 50 }): Node {
  const newId = generateId('node')

  return {
    ...node,
    id: newId,
    position: {
      x: node.position.x + offset.x,
      y: node.position.y + offset.y
    },
    selected: true,
    data: {
      ...node.data,
      // Clear group metadata to avoid conflicts
      group: undefined
    }
  }
}

/**
 * Clone an edge with a new ID and updated source/target
 */
export function cloneEdge(edge: Edge, sourceId: string, targetId: string): Edge {
  const newId = generateId('edge')

  return {
    ...edge,
    id: newId,
    source: sourceId,
    target: targetId,
    selected: true
  }
}

/**
 * Duplicate selected nodes in place (slight offset)
 */
export function duplicateNodes(
  selectedNodes: Node[],
  allEdges: Edge[],
  offset: { x: number; y: number } = { x: 30, y: 30 }
): { nodes: Node[]; edges: Edge[] } {
  if (selectedNodes.length === 0) {
    return { nodes: [], edges: [] }
  }

  const selectedNodeIds = new Set(selectedNodes.map(n => n.id))
  const idMap = new Map<string, string>()

  // Clone nodes
  const newNodes = selectedNodes.map(node => {
    const clonedNode = cloneNode(node, offset)
    idMap.set(node.id, clonedNode.id)
    return clonedNode
  })

  // Clone connecting edges
  const relevantEdges = allEdges.filter(edge =>
    selectedNodeIds.has(edge.source) && selectedNodeIds.has(edge.target)
  )

  const newEdges: Edge[] = []

  relevantEdges.forEach(edge => {
    const newSource = idMap.get(edge.source)
    const newTarget = idMap.get(edge.target)

    if (newSource && newTarget) {
      newEdges.push(cloneEdge(edge, newSource, newTarget))
    }
  })

  console.log('✨ Duplicated:', {
    nodes: newNodes.length,
    edges: newEdges.length
  })

  return { nodes: newNodes, edges: newEdges }
}
